import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import type { Listing, Paginator } from 'shared/@types';
import { useLocale } from 'shared/library/i18n/use-locale';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';
import { prepareDataForApi } from 'shared/library/api-client';
import {
  compactListingCardList,
  compactListingCardPaginator,
} from 'shared/library/compact-page-data';
import { sanitizeListingsFilterParams, type ListingsFilter } from 'shared/state/listings-filter';
import { useUserStore } from 'shared/state/user';
import type { IndexListingsScreenProps } from 'shared/screens/listings/index-listings';
import { getLanguageCodeFromPath } from 'shared/library/i18n/get-language-code-from-path';
import { getSupportedLanguageCode } from 'shared/library/i18n/languages';

type ListingIndexResult = Pick<
  IndexListingsScreenProps,
  'initialListings' | 'boostedListings' | 'totalPets' | 'initialFilter'
>;

function listingIndexResult(props: IndexListingsScreenProps): ListingIndexResult {
  return {
    initialListings: props.initialListings,
    boostedListings: props.boostedListings,
    totalPets: props.totalPets,
    initialFilter: props.initialFilter,
  };
}

function listingIndexBrowserPath(
  value: string,
  router: { locale?: string; basePath?: string },
  languageCode: string,
) {
  const pathname = value.split(/[?#]/)[0] || '/';
  const basePath = router.basePath?.replace(/\/$/, '') || '';
  const withoutBasePath =
    basePath && (pathname === basePath || pathname.startsWith(`${basePath}/`))
      ? pathname.slice(basePath.length) || '/'
      : pathname;
  const locale = getSupportedLanguageCode(router.locale) ?? languageCode;
  const localizedPath = getLanguageCodeFromPath(withoutBasePath)
    ? withoutBasePath
    : `/${locale}${withoutBasePath === '/' ? '' : withoutBasePath}`;

  return `${basePath}${localizedPath}`;
}

export function listingIndexResultKey(
  filter: ListingsFilter,
  context: { languageCode: string; countryCode: string; apiKey: string | null; pathname: string },
) {
  return JSON.stringify([
    context.languageCode,
    context.countryCode,
    context.apiKey,
    context.pathname,
    {
      ...filter,
      breeds: filter.breeds ? Array.from(new Set(filter.breeds.map(String))).sort() : null,
    },
  ]);
}

// Only web pages opt in. Native and city pages keep their existing data/navigation flow.
// A category page passes the generic index path: its filters then move the URL there while
// this page, and an open filter dialog, stay mounted. A reload renders the generic page.
export function useListingIndexResults<T extends IndexListingsScreenProps>(
  props: T,
  lockedFilters: Partial<ListingsFilter> = {},
  indexPathname?: string,
) {
  const router = useRouter();
  const { languageCode, countryCode } = useLocale();
  const apiKey = useUserStore((state) => state.apiKey);
  const { request } = useApi({ notifyOnServerErrors: false });
  const requestRef = useRef(request);
  requestRef.current = request;
  const routerRef = useRef(router);
  routerRef.current = router;
  // Next asPath can omit locale/basePath, while filter links use the browser's localized URL.
  const pathname = listingIndexBrowserPath(router.asPath, router, languageCode);
  const indexPath = indexPathname
    ? listingIndexBrowserPath(indexPathname, router, languageCode)
    : null;
  // The category's own filters apply only at its own URL, as in its SSR request.
  const routeLockedFilters = pathname === indexPath ? {} : lockedFilters;
  const context = { languageCode, countryCode, apiKey, pathname };
  const sessionKey = JSON.stringify([languageCode, countryCode, apiKey]);
  const requestedFilter = router.isReady
    ? sanitizeListingsFilterParams({ ...router.query, ...routeLockedFilters })
    : props.initialFilter;
  const key = listingIndexResultKey(requestedFilter, context);
  const serverKey = listingIndexResultKey(props.initialFilter, context);
  const serverPropsRef = useRef(props.initialListings);
  const serverPathRef = useRef(pathname);
  const serverSessionRef = useRef(sessionKey);
  const initializedRef = useRef(false);
  const serverSeedKeyRef = useRef<string | null>(null);
  const [applied, setApplied] = useState({ result: listingIndexResult(props), sessionKey });

  useEffect(() => {
    // A full navigation supplies fresh SSR props; a shallow one deliberately does not.
    const freshServerProps = serverPropsRef.current !== props.initialListings;
    if (freshServerProps) {
      serverPropsRef.current = props.initialListings;
      serverPathRef.current = pathname;
      serverSessionRef.current = sessionKey;
    }

    if (!router.isReady || (pathname !== serverPathRef.current && pathname !== indexPath)) return;

    const useServerResult =
      (!initializedRef.current || freshServerProps || serverSeedKeyRef.current === key) &&
      key === serverKey &&
      sessionKey === serverSessionRef.current;
    initializedRef.current = true;
    // Keep the initial seed across Strict Mode effect replay, until the first real URL change.
    serverSeedKeyRef.current = useServerResult ? key : null;

    if (useServerResult) {
      setApplied({ result: listingIndexResult(props), sessionKey });
      return;
    }

    const controller = new AbortController();
    const params = { ...prepareDataForApi(requestedFilter, ['breeds']), format: 'card' };

    Promise.all([
      requestRef.current<{ listings: Paginator<Listing>; total_pets: number }>(
        apiRoute('listings.index'),
        params,
        undefined,
        controller,
      ),
      requestRef.current<{ boosted_listings: Listing[] }>(
        apiRoute('listings.boosted'),
        params,
        undefined,
        controller,
      ),
    ])
      .then(([index, boosted]) => {
        if (controller.signal.aborted) return;

        const result = {
          initialListings: compactListingCardPaginator(index.listings),
          boostedListings: compactListingCardList(boosted.boosted_listings),
          totalPets: index.total_pets,
          initialFilter: requestedFilter,
        };
        setApplied({ result, sessionKey });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        controller.abort();
        // Retry through the existing SSR route rather than leave stale results at a new URL.
        void routerRef.current.replace(routerRef.current.asPath, undefined, { shallow: false });
      });

    return () => controller.abort();
  }, [key, sessionKey, serverKey, router.isReady, props.initialListings]);

  const onQueryChange = useCallback(
    (href: string) => {
      const currentRouter = routerRef.current;
      const target = listingIndexBrowserPath(href, currentRouter, languageCode);
      if (target === indexPath && indexPath !== null) {
        // Keep rendering this category route at the generic URL on every later edit too; a
        // plain shallow push to another Next route would still load the generic page.
        const query = Object.fromEntries(new URLSearchParams(href.split(/[?#]/)[1] ?? ''));
        void currentRouter.push({ pathname: currentRouter.pathname, query }, href, {
          shallow: true,
          scroll: false,
        });
        return;
      }
      void currentRouter.push(
        href,
        undefined,
        target === pathname ? { shallow: true, scroll: false } : undefined,
      );
    },
    [pathname, indexPath, languageCode],
  );

  const result =
    applied.sessionKey === sessionKey
      ? applied.result
      : {
          initialListings: { ...props.initialListings, data: [] },
          boostedListings: [],
          totalPets: 0,
          initialFilter: requestedFilter,
          loadingInitial: true,
        };

  return {
    ...props,
    ...result,
    onQueryChange,
  };
}
